/**
 * PageSettings Configuration
 *
 * This is a mechanism for updating page settings that are used to control
 * how the HTML page template (that wraps the rendered React component). This
 * is mostly used to configure things that are most important to search
 * engines.
 */
import {t} from "@lingui/core/macro";

import {RequestInfo} from "@khan/shared-core";

import {setPageMetadata} from "./page-metadata.ts";

export type PageSettings = {
    /*
     * The title of the page. When rendered it's the part at the beginning of
     * the title, like so: "TITLE | Khan Academy"
     */
    title: string;
    /*
     * The full title of the page. Set this to overwrite the entire title,
     * including the " | Khan Academy" suffix. Should be used sparingly, mostly
     * for landing pages where we want full SEO control for a page.
     */
    fullTitle: string;
    /*
     * Should we show a notification indicator in the title?
     * Ends up looking like: `* TITLE | Khan Academy`.
     */
    showTitleNotification: boolean;
    /*
     * NOTE: Every property beneath here will only have an effect during
     * server-side rendering. It won't do anything when run in the user's
     * browser (because they are largely used for configuring the initial
     * HTML payload sent down by the browser).
     */

    /*
     * The meta description of the page. Used to tell search engines, Facebook,
     * and Twitter about the page.
     */
    description: string;
    /*
     * The canonical URL for the page. This is rarely necessary, only use if
     * the page can be accessed from multiple URLs.
     */
    canonicalUrl: string;
    /*
     * Breadcrumbs for Google. Used for SEO purposes to tell Google the
     * heirarchy of the site up to this page.
     */
    breadcrumbsForGoogle: Array<{
        href: string;
        title: string;
    }>;
    /*
     * A URL to an inline video (encoded). If the page is primarily representing
     * a video then this URL should point to a raw MP4 or Flash-encoded video
     * URL. For a Youtube video this is: http://www.youtube.com/v/YOUTUBE_ID
     * If you specify videoUrl you should also specify videoEmbedUrl.
     */
    videoUrl: string;
    /*
     * A URL to an inline video (embedded). If the page is primarily
     * representing a video then this URL should point to an embedded video
     * URL. For a Youtube video this is: http://www.youtube.com/embed/YOUTUBE_ID
     * If you specify videoEmbedUrl you should also specify videoUrl.
     */
    videoEmbedUrl: string;
    /*
     * A URL to an inline image. If the page can be represented by an image then
     * this should be set to point to that URL. This is used by all of our
     * content pages. If none is specified then it falls back to:
     * /images/khan-logo-dark-background-2.png
     */
    imageUrl: string;
    /*
     * Any inline scripts to insert into the page. You should provide both the
     * script contents and the wrapping `<script>...</script>`. These will be
     * inserted at the very bottom of the page beneath all other scripts.
     */
    inlineScripts: Array<string>;
    /*
     * Should the page be indexable by search engines? Defaults to `false`.
     */
    noIndex: boolean;
    /*
     * Should search engines follow links from this page? Defaults to `false`.
     */
    noFollow: boolean;
    /*
     * Should an iOS Smart Banner be shown to the user? Defaults to `false`.
     * More details: https://developer.apple.com/library/archive/documentation/AppleApplications/Reference/SafariWebContent/PromotingAppswithAppBanners/PromotingAppswithAppBanners.html
     */
    iOSSmartBanner: boolean;
    /*
     * Control the inclusion of analytics (Google Tag Manager) in the page.
     * By default they are excluded on the dev server, to E2E test users, and
     * on embedded pages.
     */
    analytics: boolean;
    /*
     * The HTTP status code for the page.
     *
     * DO NOT remove. This is used to by PageRoot to determine if we should
     * render a full page error or not (#app-shell-package/page-root.tsx).
     */
    statusCode: number;
    /**
     * Control the display of the global Footer in the page. Defaults to `true`.
     */
    footer: boolean;
    /**
     * Control the display of the global Header in the page. Defaults to `true`.
     */
    header: boolean;
    /**
     * The background color that will be used in the global Header.
     * Defalts to white.
     */
    headerBackgroundColor: "darkBlue" | "white";
    /**
     * Set the header to be sticky (fixed to the top of the page) except on
     * small screen sizes. Excludes Site Banners, they'll scroll off the top.
     * Defaults to false.
     */
    stickyHeader: boolean;
    /**
     * Some styles, like the donation banner styles, are different for 321 pages.
     * TODO(MP-3752): remove after EOY campaign 2022.
     */
    is321: boolean;
};

export type PartialPageSettings = Readonly<Partial<PageSettings>>;

// exported for testing purposes only
export const _defaultAnalytics = (): boolean =>
    !RequestInfo.embedded && !RequestInfo.IS_DEV_SERVER && !RequestInfo.isE2E;

// Initialize the title with the title from the page, if it exists
// This will help us handle edge cases where we haven't explicitly set the
// title in JavaScript. Hopefully this will become less of a problem as more
// of our app moves to App Shell.
const finalTitle = (typeof document !== "undefined" && document.title) || "";

// The default page settings.
// Exported for testing purposes, do not directly access or modify.
// NOTE: This object should only contain constants defined in this file or values from
// DeployInfo, RequestInfo, or PermissionsInfo.  Those objects constitute part of the key
// used to cache SSR results in memcache.  If their values change we will re-SSR the page
// which means we'll use all of the values from DEFAULT_PAGE_SETTINGS and none from
// __PAGE_SETTINGS__ since it won't exist for the new render.
export const DEFAULT_PAGE_SETTINGS: Readonly<PageSettings> = {
    // sync-start:page-settings 1333258181 ./dev/tools/jest-runner/setup-window-globals-for-testing.js
    title: "",
    fullTitle: finalTitle,
    showTitleNotification: false,
    description: "",
    canonicalUrl: "",
    breadcrumbsForGoogle: [],
    videoUrl: "",
    videoEmbedUrl: "",
    imageUrl: "",
    inlineScripts: [],
    noIndex: false,
    noFollow: false,
    iOSSmartBanner: false,
    get analytics() {
        return _defaultAnalytics();
    },
    statusCode: 200,
    get footer() {
        return !RequestInfo.embedded;
    },
    get header() {
        return !RequestInfo.embedded;
    },
    headerBackgroundColor: "white",
    stickyHeader: false,
    // sync-end:page-settings
    is321: false,
};

const _pageSettings: PageSettings = {
    ...DEFAULT_PAGE_SETTINGS,
    // We override the default page settings with any values that've been set
    // using the __PAGE_SETTINGS__ object. This is only populated on a
    // successful server-side render.
    // @ts-expect-error - TS2339 - Property '__PAGE_SETTINGS__' does not exist on type 'Window & typeof globalThis'.
    ...((typeof window !== "undefined" && window.__PAGE_SETTINGS__) ||
        ({} as any)),
};

/**
 * resetPageSettings: Reset the page settings back to their default values.
 * Should only be called by a single place in the application, likely on page
 * navigation using React Router.
 */
export const resetPageSettings = (): void => {
    // eslint-disable-next-line no-restricted-syntax
    updatePageSettingsInternal(DEFAULT_PAGE_SETTINGS);
};

/**
 * getPageSettings: Get the current page settings. Should almost certainly not
 * be directly used, will only be used by the server-side rendering page logic
 * to get the values for building the HTML page template.
 */
export const getPageSettings = (): Readonly<PageSettings> => {
    // Copy over the settings to a new object to avoid modification
    const {inlineScripts, breadcrumbsForGoogle, ...rest} = _pageSettings;

    /**
     * We need to deep copy the complex properties or we end up exposing
     * ourselves to external mutation as well as pollution of settings
     * returned by earlier calls, causing side effects in code that was not
     * expecting them.
     */
    return {
        ...rest,
        breadcrumbsForGoogle: breadcrumbsForGoogle.map((bfg) => ({...bfg})),
        inlineScripts: [...inlineScripts],
    };
};

const areObjectsEqual = <T extends Record<any, any> | Array<any>>(
    obj1: T,
    obj2: T,
): boolean => {
    if (Object.keys(obj1 as any).length !== Object.keys(obj2 as any).length) {
        return false;
    }

    return Object.entries(obj1).every(([key, value]) => {
        if (!Object.prototype.hasOwnProperty.call(obj2, key)) {
            return false;
        }

        if (obj2[key as any] === value) {
            return true;
        }

        if (typeof value === "object") {
            return areObjectsEqual(value, obj2[key as any]);
        }

        return false;
    });
};

/**
 * Determines if two sets of page settings are equal.
 * @param {PageSettings} settings1 One set of settings to compare
 * @param {PageSettings} settings2 The other set of settings to compare
 * @returns {boolean} `true` if the sets are equal; otherwise, `false`.
 */
export const areEqual = (
    settings1: Readonly<PageSettings>,
    settings2: Readonly<PageSettings>,
): boolean => areObjectsEqual(settings1, settings2);

/**
 * Take a full page settings object and union it with some additional settings.
 */
export const previewSettings = (
    newSettings: PartialPageSettings,
    baseSettings: Readonly<PageSettings>,
): Readonly<PageSettings> => {
    const finalSettings: Partial<PageSettings> = {};

    // Copy the base settings
    // NOTE: This isn't a deep copy. It probably should be.
    Object.assign(finalSettings, baseSettings);

    // Copy the new settings on top.
    // NOTE: This too isn't a deep copy but it probably should be.
    Object.assign(finalSettings, newSettings);

    // Configure the fullTitle correctly based on what the user has specified
    if (finalSettings.title) {
        finalSettings.fullTitle = `${finalSettings.title} | ${t`Khan Academy`}`;
        // Immediately reset the title as we only use it to build the fullTitle
        finalSettings.title = "";
    } else if (!finalSettings.fullTitle) {
        finalSettings.fullTitle = t`Khan Academy`;
    }
    if (
        RequestInfo.IS_DEV_SERVER &&
        !finalSettings.fullTitle.includes("[DEV]")
    ) {
        finalSettings.fullTitle = `[DEV] ${finalSettings.fullTitle}`;
    }

    // We know we built full settings. Let TypeScript know with cast to any.
    return finalSettings as any;
};

/**
 * updatePageSettingsInternal: The primary method used to update the page
 * settings. You can specify as many properties as you like, anything left
 * unspecified will just use the default values instead.
 *
 * NOTE: You probably should not use this function directly! Use PageDisplay or
 * useUpdatePageSettings in data-access instead.
 *
 * @param {PartialPageSettings} settings The settings to update.
 * @param {boolean} [reset] Whether to reset settings to defaults before
 * applying the new settings. Defaults to `false`.
 */
export const updatePageSettingsInternal = (
    settings: PartialPageSettings,
    reset?: boolean | null,
): void => {
    // Build the new settings.
    const newPageSettings = reset
        ? previewSettings(settings, DEFAULT_PAGE_SETTINGS)
        : previewSettings(settings, _pageSettings);

    // Copy over the new settings
    Object.assign(_pageSettings, newPageSettings);

    // Set all the page meta tags based on the new settings
    setPageMetadata(_pageSettings);
};
